import * as React from 'react';
import type {PaymentBalance} from 'src/types/referral-v2';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useSelector, useDispatch} from 'react-redux';

import {
  getPaymentBalance,
  savePaypalCredentials,
  updateMinimumBalanceThreshold,
} from 'src/action-creators/referral-v2/referral-payment';
import {
  selectPaymentBalance,
  selectPaymentBalanceError,
  selectPaymentBalanceLoading,
} from 'src/selectors/referral-v2';

import {
  CollapsibleCard,
  CollapsibleCardContent,
} from '@spaced-out/ui-design-system/lib';
import {getPaypalSetupInformation} from 'src/utils/referral-v2';
import {
  BodyMedium,
  SubTitleLarge,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import PaypalIcon from 'src/images/designSystems2021/paypal.svg';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';

import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import css from 'src/components/referral-v2/settings/settings.css';


const ReferralPaymentSettings = () => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(getPaymentBalance());
  }, []);

  // @ts-expect-error - TS2322 - Type 'PaymentBalance | {}' is not assignable to type 'PaymentBalance'.
  const paymentBalance: PaymentBalance =
    useSelector(selectPaymentBalance) || {};
  const paymentBalanceLoading = useSelector(selectPaymentBalanceLoading);
  const paymentBalanceError = useSelector(selectPaymentBalanceError);

  return (
    <div className={css.paymentCard}>
      <CollapsibleCard
        title={
          <Title
            // @ts-expect-error - TS2322 - Type 'GenericError | null | undefined' is not assignable to type 'boolean'.
            isSetupPending={paymentBalanceError}
            balance={paymentBalance.balance}
            minimumThreshold={paymentBalance.minimum_balance_threshold}
            paymentBalanceLoading={paymentBalanceLoading}
          />
        }
      >
        <CollapsibleCardContent>
          {paymentBalanceError ? (
            <PaymentSetup />
          ) : (
            <PaymentStatus
              balance={paymentBalance.balance}
              minimumBalance={paymentBalance.minimum_balance_threshold}
              paymentBalanceLoading={paymentBalanceLoading}
            />
          )}
        </CollapsibleCardContent>
      </CollapsibleCard>
    </div>
  );
};
export default ReferralPaymentSettings;

const PaymentSetup = () => {
  const {t} = useLabelTranslation();
  const paypalSetupInformation = getPaypalSetupInformation(t);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [apiKey, setApiKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');

  const dispatch = useDispatch();
  const clearInputs = () => {
    setApiKey('');
    setSecretKey('');
  };

  const saveDetails = async () => {
    const payload = {
      client_id: apiKey,
      client_secret: secretKey,
    } as const;

    await dispatch(savePaypalCredentials(payload));
    setIsSubmitting(false);
    dispatch(getPaymentBalance());
    clearInputs();
  };

  return (
    <div className={css.paymentSetup}>
      <div className={css.inputActionContainer}>
        <div className={css.inputsContainer}>
          <Input
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            label={t('API_Key', 'API Key')}
            placeholder={t('Enter_API_Key_here', 'Enter API Key here')}
          />
          <Input
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            label={t('Secret_Key', 'Secret Key')}
            placeholder={t('Enter_Secret_Key_here', 'Enter Secret Key here')}
          />
        </div>
        <div className={css.actionContainer}>
          <Button
            type="tertiary"
            classNames={{wrapper: css.minimumBalanceButton}}
          >
            {t('Don\'t_have_account', 'Don\'t have account')}
          </Button>
          <Button
            type="primary"
            classNames={{wrapper: css.minimumBalanceButton}}
            onClick={saveDetails}
            isLoading={isSubmitting}
            disabled={!apiKey || !secretKey}
          >
            {t('Submit', 'Submit')}
          </Button>
        </div>
      </div>

      <div className={css.divider}></div>

      <div className={css.information}>
        <SubTitleSmall className={css.headerTextColor}>
          {t(
            'Here_is_how_to_get_the_credentials',
            'Here is how to get the credentials',
          )}
        </SubTitleSmall>

        <div className={css.informationList}>
          {paypalSetupInformation.map((info) => (
            <BodyMedium key={info.id}>
              <Badge fill="blue" text={info.id} />
              <span className={css.setupInfo}>{info.text}</span>
              {info.link && (
                <Link
                  as="bodyMedium"
                  underline={false}
                  target={'_blank'}
                  href={info.link.url}
                >
                  {info.link.text}
                  <Icon
                    color="clickable"
                    name="arrow-up-right-from-square"
                    size="medium"
                    type="regular"
                  />
                </Link>
              )}
            </BodyMedium>
          ))}
        </div>
      </div>
    </div>
  );
};

const PaymentStatus = ({
  balance,
  minimumBalance,
  paymentBalanceLoading,
}: {
  balance: number;
  minimumBalance: number;
  paymentBalanceLoading: boolean;
}) => {
  const {t} = useLabelTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLocked, setIsLocked] = React.useState(!!minimumBalance);
  const dispatch = useDispatch();
  const [minBalanceInput, setMinBalanceInput] = React.useState(minimumBalance);

  React.useEffect(() => {
    if (minimumBalance) {
      setMinBalanceInput(minimumBalance);
    }
  }, [minimumBalance]);

  const handleEditThreshold = async () => {
    if (isLocked) {
      setIsLocked(false);
    } else {
      const payload = {
        minimum_balance_threshold: minBalanceInput,
      } as const;
      setIsSubmitting(true);
      await dispatch(updateMinimumBalanceThreshold(payload));
      setIsSubmitting(false);
      setIsLocked(true);
    }
  };

  return (
    <div className={css.paymentBalance}>
      <div className={css.paymentStatus}>
        <div className={css.totalBalance}>
          <SubTitleLarge>{t('Total_Balance', 'Total Balance')}</SubTitleLarge>
          {paymentBalanceLoading ? (
            <SubTitleLarge>{`${t('Loading', 'Loading')}...`}</SubTitleLarge>
          ) : (
            <SubTitleLarge
              color={minimumBalance > balance ? 'danger' : 'success'}
            >
              ${balance}
            </SubTitleLarge>
          )}
        </div>
        <Link
          href={
            'https://www.paypal.com/us/digital-wallet/manage-money/add-cash'
          }
          underline={false}
          target="_blank"
        >
          <Button iconRightName="arrow-up-right-from-square" type="primary">
            {t('Add_Balance', 'Add Balance')}
          </Button>
        </Link>
      </div>

      <div className={css.divider}></div>

      <div className={css.minimumBalance}>
        <Input
          value={minBalanceInput ? minBalanceInput.toString() : ''}
          type="number"
          onChange={(e) => setMinBalanceInput(parseFloat(e.target.value))}
          locked={isLocked}
          label={t('Set_threshold_notification', 'Set threshold notification')}
          placeholder={t('Enter_minimum_value', 'Enter minimum value')}
          helperText={t(
            'You_will_be_notified_If_the_balance_falls_below_threshold',
            'You will be notified If the balance falls below threshold',
          )}
        />

        <Button
          type={minimumBalance ? 'secondary' : 'tertiary'}
          classNames={{wrapper: css.minimumBalanceButton}}
          disabled={!minimumBalance || !minBalanceInput}
          isLoading={isSubmitting}
          onClick={handleEditThreshold}
        >
          {isLocked ? t('Edit', 'Edit') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
};

const Title = ({
  isSetupPending,
  balance,
  minimumThreshold,
  paymentBalanceLoading,
}: {
  isSetupPending: boolean;
  balance: number;
  minimumThreshold: number;
  paymentBalanceLoading: boolean;
}) => {
  const {t} = useLabelTranslation();
  return (
    <span className={css.paypalHeader}>
      <PaypalIcon />
      <SubTitleLarge>{t('Paypal', 'Paypal')}</SubTitleLarge>
      {isSetupPending ? (
        <Chip semantic="danger">{t('Setup_Pending', 'Setup Pending')}</Chip>
      ) : (
        <>
          {paymentBalanceLoading ? (
            <Chip>{t('Loading', 'Loading')}...</Chip>
          ) : (
            <div>
              {minimumThreshold !== undefined && minimumThreshold > balance ? (
                <Chip semantic="danger">{t('Low_Balance', 'Low Balance')}</Chip>
              ) : (
                <Chip semantic="success">{`${t(
                  'Balance',
                  'Balance',
                )} ${balance}`}</Chip>
              )}
            </div>
          )}
        </>
      )}
    </span>
  );
};
